import classNames from 'classnames';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import customerLogo from '../../assets/customerLogo.png';
import merchatLogo from '../../assets/merchatLogo.png';
import { BrandingIconCard, CurrencySymbol, ResponsiveImage } from '../../components';
import IconCollection from '../../components/IconCollection/IconCollection';
import useCategoryConfigMap from '../../hooks/useCategoryConfigMap';

// Inline Lucide-style icons (no new dependency).
// Lovable uses MapPin / Star / Clock here — these mirror those glyphs.
const ClockIcon = props => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  >
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const MapPinIcon = props => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  >
    <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const StarFilledIcon = props => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  >
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { onUpdateAdClick } from '../../util/api';
import { formatListingPriceAmount } from '../../util/currency';
import { configForListing, convertToTitleCase } from '../../util/data';
import { injectIntl } from '../../util/reactIntl';
import { createResourceLocatorString } from '../../util/routes';
import { mergeFromIntoIntent, POST_AUTH_ACTIONS } from '../../util/postAuthIntent';
import { DEAL_LISTING_TYPE, PRODUCT_LISTING_TYPE, SERVICE_LISTING_TYPE } from '../../util/types';
import { createSlug } from '../../util/urlHelpers';
import css from './AlgoliaSearchDealCard.module.css';

const PERCENTAGE_DISCOUNT_TYPE = 'percentage-discount';
const ROOT_URL = process.env.REACT_APP_MARKETPLACE_ROOT_URL;

const toRadians = value => (value * Math.PI) / 180;

const getDistanceInMeters = (origin, destination) => {
  if (!origin?.lat || !origin?.lng || !destination?.lat || !destination?.lng) {
    return null;
  }

  const earthRadiusInMeters = 6371000;
  const latitudeDelta = toRadians(destination.lat - origin.lat);
  const longitudeDelta = toRadians(destination.lng - origin.lng);
  const originLatitude = toRadians(origin.lat);
  const destinationLatitude = toRadians(destination.lat);

  const haversineValue =
    Math.sin(latitudeDelta / 2) * Math.sin(latitudeDelta / 2) +
    Math.cos(originLatitude) *
      Math.cos(destinationLatitude) *
      Math.sin(longitudeDelta / 2) *
      Math.sin(longitudeDelta / 2);

  const angularDistance = 2 * Math.atan2(Math.sqrt(haversineValue), Math.sqrt(1 - haversineValue));
  return Math.round(earthRadiusInMeters * angularDistance);
};

const getCountdownText = endUnix => {
  if (!endUnix || Number.isNaN(Number(endUnix))) return '';

  const nowInSeconds = Math.floor(Date.now() / 1000);
  const remainingSeconds = Number(endUnix) - nowInSeconds;

  if (remainingSeconds <= 0) return 'Expired';

  const days = Math.floor(remainingSeconds / 86400);
  const hours = Math.floor((remainingSeconds % 86400) / 3600);
  const minutes = Math.floor((remainingSeconds % 3600) / 60);
  return `${days}d ${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m`;
};
/**
 * Component to display a deal or service card with details fetched from Algolia search.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.hit - The deal or service data from Algolia.
 * @param {Object} props.currentUser - The current logged-in user.
 * @param {Function} props.toggleFavorite - Function to toggle the deal/service as a favorite.
 * @param {Array} props.updatedDealCategories - Updated deal categories including color and description.
 *
 * @returns {JSX.Element} The rendered component.
 */
const AlgoliaSearchDealCard = ({
  hit,
  roleName,
  currentUser,
  toggleFavorite,
  updatedDealCategories,
  userLocation,
  history,
  routeConfiguration: routes,
  isBookingLink,
  intl,
}) => {
  const ownListing = hit?.author?.id === currentUser?.id?.uuid;
  // Destructure necessary fields from hit object
  const { title, price, images = [], author, publicData } = hit || {};

  // Deals carry the linked service listing's subcategory in listingsCategories,
  // so they resolve against the same slug-keyed config map as services. Joined
  // at render time so an admin config edit needs no listing or index rewrite.
  const categoryConfigMap = useCategoryConfigMap();
  const listingConfig = configForListing(categoryConfigMap, publicData);
  const {
    discount,
    listingType,
    percentage_discount,
    categoryLevel1,
    categoryLevel2,
    original_price,
    dealType,
    totalReviews = 0,
    averageRating = 0,
    deal_end_date,
  } = publicData || {};

  const locationText =
    publicData?.location && typeof publicData.location === 'object'
      ? publicData.location.address || publicData.location.city || ''
      : typeof publicData?.location === 'string'
        ? publicData.location
        : '';

  const distanceMeters =
    hit?._rankingInfo?.geoDistance ||
    getDistanceInMeters(userLocation, hit?._geoloc);
  const distanceText =
    typeof distanceMeters === 'number' && Number.isFinite(distanceMeters)
      ? distanceMeters >= 1000
        ? `${(distanceMeters / 1000).toFixed(1)} km`
        : `${Math.round(distanceMeters)} m`
      : '';

  const { currency, amount } = price || {};
  const { displayName: authorName, publicData: authorPublicData } = author || {};
  const authorDisplayName =
    authorPublicData?.business_name ||
    authorName ||
    [author?.firstName, author?.lastName].filter(Boolean).join(' ') ||
    '';

  const isServiceDeal = dealType === SERVICE_LISTING_TYPE;
  const routeConfiguration = useRouteConfiguration();
  // Check if the deal type is a percentage discount
  const isPercentageDiscount =
    categoryLevel2 === PERCENTAGE_DISCOUNT_TYPE || listingType === PERCENTAGE_DISCOUNT_TYPE;
  const userType = currentUser?.attributes?.profile?.publicData?.userType || '';
  // Determine the correct wishlist type based on listingType
  // Determine the correct wishlist type based on listingType
  const wishlistTypeKey =
    listingType === SERVICE_LISTING_TYPE
      ? 'wishlist_service_ids'
      : listingType === DEAL_LISTING_TYPE
        ? 'wishlist_deal_ids'
        : listingType === PRODUCT_LISTING_TYPE
          ? 'wishlist_product_ids'
          : null;

  const wishlist = currentUser?.attributes?.profile?.publicData?.[wishlistTypeKey] || [];

  // Check if this listing is already marked as a favorite
  const isFavorite = wishlist.includes(hit?.objectID);

  // Generate slug for the listing page URL
  const listingId = hit?.objectID;
  const listingSlug = createSlug(title);

  // Find the corresponding subcategory and get its color
  const subcategory =
    updatedDealCategories
      ?.find(cat => cat.subcategories.some(sub => sub.id === categoryLevel2))
      ?.subcategories.find(sub => sub.id === categoryLevel2) || {};
  const subcategoryColor = subcategory.color || '#000'; // Default to black if no color is found

  /**
   * Handles the click event for adding/removing a listing to/from favorites.
   */
  const listingUrl = createResourceLocatorString('ListingPage', routes, { id: listingId, slug: listingSlug },
    isBookingLink ? { bookingLink: true } : {});

  const handleFavoriteClick = () => {
    if (currentUser === null) {
      mergeFromIntoIntent(listingUrl, POST_AUTH_ACTIONS.BOOK);
      history.push(createResourceLocatorString('LoginPage', routes, {}, {}), { from: listingUrl });
    } else {
      toggleFavorite(hit?.objectID, listingType, currentUser, hit?.author?.id, title, images);
    }
  };
  const arabicLanguage = typeof window !== 'undefined' && localStorage.getItem("language") === "Arabic";

  const listingUrlFull = ROOT_URL + listingUrl;
  const [countdownText, setCountdownText] = React.useState(() => getCountdownText(deal_end_date));
  const isExpired = countdownText === 'Expired';
  const endsInLabel = arabicLanguage ? 'ينتهي خلال' : 'Ends in';
  const expiredLabel = arabicLanguage ? 'منتهي' : 'Expired';

  const goToListing = () => {
    history.push(
      createResourceLocatorString(
        'ListingPage',
        routeConfiguration,
        { id: listingId, slug: listingSlug },
        isBookingLink ? { bookingLink: true } : {}
      )
    );
  };

  React.useEffect(() => {
    setCountdownText(getCountdownText(deal_end_date));

    const timerId = setInterval(() => {
      setCountdownText(getCountdownText(deal_end_date));
    }, 1000);

    return () => clearInterval(timerId);
  }, [deal_end_date]);

  const whatsappMessage = `Hello NOW Offers! , I’d like to book this deal *${title}* for *${currency} ${(amount / 100)?.toFixed(2)}* by this merchant *${authorPublicData?.business_name}* on your platform. Please confirm availability. Thanks!
  ${listingUrlFull}`;

  return (
    <div className={classNames(css.dealCardWrapper, arabicLanguage && css.arabicDirection)}>
      <div className={css.root}>
          <div className={css.jobCard} style={{ '--dealAccentColor': subcategoryColor }}>
            <div className={css.textContent}
              onClick={async() => {
                if(hit?.isPromoted){  
                  if(hit?.adId){
                    await onUpdateAdClick({ adId: hit?.adId });
                  }
                }

                history.push(
                  createResourceLocatorString(
                    'ListingPage',
                    routeConfiguration,
                    { id: listingId, slug: listingSlug },
                    isBookingLink ? { bookingLink: true } : {}
                  )
                );
                if (currentUser && userType == 'customer') {
                  // trackEventsMixpanel(mixPanelEventsConstants.DEALS, {
                  //   screen: mixPanelEventsScreens.ALOGLIA_SEARCH_PAGE,
                  //   user_id: currentUser?.id.uuid,
                  //   user_type: 'Customer',
                  //   action_type: mixPanelEventsConstants.OPEN_DEAL
                  // });
                }
              }}
            >
              <div className={css.imageWrapper}>
                {images.length && images[0] ? (
                  <ResponsiveImage
                    alt={title}
                    image={images[0]}
                    variants="custom-image"
                    width="100%"
                  />
                ) : (
                  userType === 'customer' ? (
                    <ResponsiveImage
                      alt="card image"
                      localImageSrc={customerLogo}
                      className={css.cardImage}
                      variants="custom-image"
                    />
                  ) : (
                    <ResponsiveImage
                      alt="card image"
                      localImageSrc={merchatLogo}
                      className={css.cardImage}
                      variants="custom-image"
                    />
                  )
                )}

                {/* Discount tag (top-left) — rounded-2xl yellow primary */}
                {isPercentageDiscount ? (
                  <div className={classNames(css.percentage, css.percentagePercentage)}>
                    -{percentage_discount ? percentage_discount : discount}%
                  </div>
                ) : (
                  <div className={classNames(css.percentage, css.percentageAmount)}>
                    <CurrencySymbol size={12} color="#0e1220" />{' '}
                    -{formatListingPriceAmount(intl, original_price ? original_price - amount / 100 : 0)}
                  </div>
                )}

                {/* Countdown overlay (bottom of image) — dark gradient with Clock icon */}
                {countdownText ? (
                  <div
                    className={classNames(
                      css.countdownOverlay,
                      isExpired && css.countdownExpired
                    )}
                  >
                    <ClockIcon className={css.countdownIcon} />
                    <span>
                      {isExpired ? expiredLabel : `${endsInLabel} ${countdownText}`}
                    </span>
                  </div>
                ) : null}
              </div>

              {/* Card body */}
              <div className={css.cardBody}>
                <h3 className={css.dealTitle}>{title}</h3>

                {authorDisplayName || locationText ? (
                  <div className={css.locationRow} title={[authorDisplayName, locationText].filter(Boolean).join(' · ')}>
                    <MapPinIcon className={css.locationIcon} />
                    <span className={css.locationText}>
                      {authorDisplayName ? (
                        <span className={css.businessName}>{authorDisplayName}</span>
                      ) : null}
                      {authorDisplayName && locationText ? ' · ' : null}
                      {locationText || null}
                    </span>
                    {distanceText ? (
                      <span className={css.distanceText}>{distanceText}</span>
                    ) : null}
                  </div>
                ) : null}

                {/* Rating — single filled yellow star + value + (count) */}
                <div className={css.reviewDetails}>
                  <StarFilledIcon className={css.starIcon} />
                  <span className={css.ratingValue}>
                    {(Number(averageRating) || 0).toFixed(1)}
                  </span>
                  <span className={css.reviewCount}>
                    ({Number(totalReviews) || 0})
                  </span>
                </div>

                {/* Price + Book row */}
                <div className={css.priceBookRow}>
                  <div className={css.priceWrapper}>
                    {original_price ? (
                      <span className={css.actualPrice}>
                        <CurrencySymbol size={11} />{' '}
                        {formatListingPriceAmount(intl, original_price)}
                      </span>
                    ) : null}
                    <span className={css.dealPrice}>
                      <CurrencySymbol size={16} />{' '}
                      {amount ? formatListingPriceAmount(intl, amount / 100) : '0'}
                      {listingConfig?.transactionModel === 'hourly' ? (
                        <FormattedMessage id="ListingCard.perHourSuffix" />
                      ) : null}
                    </span>
                  </div>
                  <button
                    type="button"
                    className={css.bookNowButton}
                    onClick={e => {
                      e.stopPropagation();
                      goToListing();
                    }}
                  >
                    <FormattedMessage id="PopularDeals.bookNow" />
                  </button>
                </div>
              </div>
            </div>

            {isBookingLink && isServiceDeal ?
              <div className={css.bookOnWhatsapp}
                onClick={(e) => {
                  e.stopPropagation();
                  typeof window !== undefined && window.open(`https://wa.me/966539900466?text=${whatsappMessage}`, '_blank');
                }}>
                <IconCollection name="whatsapp" />
                <span><FormattedMessage id='ServiceCard.bookOnWhatsapp' /></span>
              </div> : null}

            {!ownListing && <span
              className={classNames(css.favoriteBtn, isFavorite && css.favoriteBtnActive)}
              role="button"
              tabIndex={0}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                handleFavoriteClick();
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleFavoriteClick();
                }
              }}
              aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            >
              <BrandingIconCard type={isFavorite ? 'red-heart' : 'heart'} />
            </span>}
          </div>
        </div>
    </div>
  );
};

export default injectIntl(AlgoliaSearchDealCard);
